import { useContext, useEffect, useMemo, useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router';
import { Menu } from 'lucide-react';
import { useTranslations } from '@/lib/i18n/useTranslations';

import { MainNavigation } from '@/modules/navigation/components/main-navigation/MainNavigation';
import { MobileNavigation } from '@/modules/navigation/components/main-navigation/MobileNavigation';
import { canReadModule } from '@/modules/users/utils/permissions';
import { getModuleFromPath } from '@/modules/navigation/utils/route-permissions';
import { useAuthStore } from '@/store/auth.store';
import { EntityActionHeader } from '@/modules/ui/components/EntityActionHeader';
import { EntityHeaderContext } from '@/modules/ui/entity-header/EntityHeaderContext';
import { EntityHeaderProvider } from '@/modules/ui/entity-header/EntityHeaderProvider';
import { BreadcrumbsProvider } from '@/modules/ui/breadcrumbs/BreadcrumbsProvider';
import { BreadcrumbsContext } from '@/modules/ui/breadcrumbs/BreadcrumbsContext';
import { Breadcrumbs, TooltipProvider } from '@oniratec/onira-react-ui';
import { useBreadcrumb } from '@/modules/ui/breadcrumbs/useBreadcrumb';
import { EntityActionFooter } from '@/modules/ui/components/EntityActionFooter';
import { EntityFooterContext } from '@/modules/ui/entity-footer/EntityFooterContext';
import { EntityFooterProvider } from '@/modules/ui/entity-footer/EntityFooterProvider';
import { getAuthToken } from '@/lib/auth/authGuard';
import { useIsPwa } from '@/lib/pwa/useIsPwa';

function DashboardFooterSlot() {
  const ctx = useContext(EntityFooterContext);
  const isPwa = useIsPwa();
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    if (!ctx) {
      return;
    }
    return ctx.subscribe(() => forceUpdate(n => n + 1));
  }, [ctx]);

  const config = ctx?.configRef.current;
  if (!config || isPwa) {
    return null;
  }
  return <EntityActionFooter config={config} />;
}

function DashboardHeaderSlot() {
  const ctx = useContext(EntityHeaderContext);
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    if (!ctx) {
      return;
    }
    return ctx.subscribe(() => forceUpdate(n => n + 1));
  }, [ctx]);

  const config = ctx?.configRef.current;
  if (!config) {
    return null;
  }
  return <EntityActionHeader config={config} />;
}

function DashboardBreadcrumbsSlot({ onOpenMobileNav }: { onOpenMobileNav: () => void }) {
  const ctx = useContext(BreadcrumbsContext);
  const navigate = useNavigate();
  const t = useTranslations('Navigation');

  const breadcrumbItem = useMemo(
    () => ({ label: 'Inicio', onClick: () => navigate('/dashboard') }),
    [navigate],
  );

  useBreadcrumb(breadcrumbItem);

  return (
    <div className="flex items-center gap-2 border-b p-2 bg-white">
      <button
        type="button"
        onClick={onOpenMobileNav}
        className="lg:hidden rounded-md p-1 text-slate-600 hover:bg-slate-100"
      >
        <Menu className="h-8 w-8" strokeWidth={1.5} />
        <span className="sr-only">{t('openMenu')}</span>
      </button>
      <Breadcrumbs items={ctx?.items ?? []} />
    </div>
  );
}

export function DashboardLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAuthStore(s => s.user);
  const logout = useAuthStore(s => s.logout);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const token = getAuthToken();

  useEffect(() => {
    const vv = window.visualViewport;
    const update = () => {
      const h = vv ? vv.height : window.innerHeight;
      document.documentElement.style.setProperty('--app-height', `${h}px`);
      // iOS scrolls window even with overflow:hidden on body; reset to prevent gap
      if (window.scrollY !== 0) {
        window.scrollTo(0, 0);
      }
    };
    update();
    vv?.addEventListener('resize', update);
    vv?.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    return () => {
      vv?.removeEventListener('resize', update);
      vv?.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  useEffect(() => {
    if (!token || !user) {
      return;
    }

    if (user.mustChangePassword) {
      if (location.pathname !== '/settings/user') {
        navigate('/settings/user?password=required', { replace: true });
      }
      return;
    }

    const moduleName = getModuleFromPath(location.pathname);
    if (!moduleName) {
      return;
    }

    if (!canReadModule(user, moduleName)) {
      navigate('/dashboard', { replace: true });
    }
  }, [location.pathname, user, navigate, token]);

  if (!token) {
    return <Navigate to="/auth/login" replace />;
  }

  return (
    <TooltipProvider delayDuration={200}>
      <BreadcrumbsProvider>
        <EntityHeaderProvider>
          <EntityFooterProvider>
            <div
              className="dashboard-shell fixed inset-x-0 top-0 flex overflow-hidden"
              style={{ height: 'var(--app-height, 100dvh)' }}
            >
              <div className="hidden lg:flex">
                <MainNavigation />
              </div>

              <MobileNavigation
                isOpen={isMobileNavOpen}
                onClose={() => setIsMobileNavOpen(false)}
                user={
                  user ? { name: user.name, email: user.email, avatarUrl: user.avatarUrl } : null
                }
                onLogout={() => {
                  logout();
                  navigate('/auth/login');
                }}
              />

              <div className="flex flex-1 flex-col bg-slate-50 overflow-hidden">
                <main className="flex-1 overflow-y-auto overscroll-contain">
                  <DashboardBreadcrumbsSlot onOpenMobileNav={() => setIsMobileNavOpen(true)} />
                  <DashboardHeaderSlot />
                  <div className="mx-auto w-full max-w-full px-6 py-6">
                    <Outlet />
                  </div>
                </main>
                <DashboardFooterSlot />
              </div>
            </div>
          </EntityFooterProvider>
        </EntityHeaderProvider>
      </BreadcrumbsProvider>
    </TooltipProvider>
  );
}
